import {Alert, createStyles, Group, Tabs, Text, TextInput} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import {MODEL_RUN_SOURCE} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import FolderSelector from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getInsufficientCreditsAgentTypeError} from 'llm/chat-library/MeteringUtils';
import {MAX_BOLTZGEN_DESIGN_PROTEIN_LENGTH} from 'model-hub/generative-models/GenerativeModelsConstants';
import {PROTEIN_STRUCTURE_PREDICTION_PERMISSIONS_QUERY_WRAPPER} from 'model-hub/ModelHubQueries';
import BioentitySearchView, {clearBioentitySearchViewSearchParams} from 'molbio/BioentitySearchView';
import notifications from 'notifications';
import {ListableItem, ListableItemExtras} from 'search/SearchConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, GRAY_SEMIDARK, RED} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import NumericInput from 'ui-library/inputs/NumericInput';
import Modal from 'ui-library/modals/Modal';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';
import APIUtil from 'util/APIUtil';
import * as net from 'util/Net';
import {NumericInputChangeEvent} from 'util/Types';
import {WorklistType} from 'worklists/WorklistConstants';
import {fetchWorklist} from 'worklists/WorklistDAO';
import WorklistSearchInput from 'worklists/WorklistSearchInput';

import {
  getMaxBulkPredictionItems,
  getModelDisplayName,
  getModelIconUrl,
  getUseMSAForSubmission,
  isMSAConfigurable,
  SCIENTIFIC_MODEL,
  supportsMSA,
  supportsMultimers,
} from '../ScientificModelConstants';
import type {BulkPredictionItem} from './BulkPredictionUtils';
import {getAvailableTypesForModel, isEntityTypeCompatible, submitBulkPrediction} from './BulkPredictionUtils';
import SelectedEntitiesArea from './SelectedEntitiesArea';
import MSAOptions from './structure-prediction/MSAOptions';

enum TabValue {
  SEARCH = 'search',
  WORKLIST = 'worklist',
}

interface BulkPredictionModalProps {
  model: string;
  inputTypes: Array<string>;
  onRequestClose: () => void;
  onRequestSubmit: (success: boolean) => void;
}

export default function BulkPredictionModal({
  model,
  inputTypes,
  onRequestClose,
  onRequestSubmit,
}: BulkPredictionModalProps): JSX.Element {
  const {classes} = useStyles();
  const {classes: tabClasses} = useTabStyles();

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    () => FolderDefaults.get(FolderDefaultsKey.BULK_MODEL_PREDICTION) ?? null
  );
  const [batchName, setBatchName] = useState<string>('');
  const [items, setItems] = useState<Array<BulkPredictionItem>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>(TabValue.SEARCH);
  const [creditError, setCreditError] = useState<{title: string; detail: string} | null>(null);
  const [msaEnabled, setMSAEnabled] = useState<boolean>(() => supportsMSA(model));

  const isBoltzGen = model === SCIENTIFIC_MODEL.BOLTZGEN;
  const [minLength, setMinLength] = useState<number>(20);
  const [maxLength, setMaxLength] = useState<number>(50);
  const [rangeValidationMessage, setRangeValidationMessage] = useState<string | null>(null);
  const maxBulkPredictionItems = getMaxBulkPredictionItems(model);

  const availableTypes = useMemo(() => getAvailableTypesForModel(inputTypes), [inputTypes]);
  const isAtCapacity = items.length >= maxBulkPredictionItems;
  const displayName = getModelDisplayName(model);
  const iconUrl = getModelIconUrl(model);
  const showModelParameters = isBoltzGen || isMSAConfigurable(model);
  const modelSupportsMultimers = supportsMultimers(model);

  const selectedBioentityIds = useMemo(() => items.map((item) => item.id), [items]);

  useEffect(() => {
    clearBioentitySearchViewSearchParams(availableTypes, Section.MODEL_HUB_STRUCTURE_PREDICTION);
  }, [availableTypes]);

  useEffect(() => {
    setMSAEnabled(supportsMSA(model));
    let cancelled = false;
    net
      .postJSONPromise({
        url: '/1/api/model-hub/prediction-batch:generate-name',
        data: {model},
      })
      .then((resp: {defaultName: string}) => {
        if (!cancelled) {
          setBatchName((prev) => (prev === '' ? resp.defaultName : prev));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [model]);

  const handleBioentitySelectionChange = useCallback(
    (newSelectedIds: Array<string>) => {
      if (newSelectedIds.length > maxBulkPredictionItems) {
        notifications.error(
          `Maximum of ${maxBulkPredictionItems} entities selected. Deselect an entity to swap in this one.`
        );
        return;
      }
      setItems(newSelectedIds.map((id) => ({id, entityType: APIUtil.apiIdToType(id)})));
    },
    [maxBulkPredictionItems]
  );

  const isItemInvalid = useCallback((item: ListableItem) => {
    if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.PROTEIN) {
      return (
        (item.permissions as ListableItemExtras['permissions'] & {structurePredictionEditable?: boolean})
          .structurePredictionEditable !== true
      );
    }
    return false;
  }, []);

  const getInvalidItemTooltipText = useCallback(
    (item: ListableItem): string | undefined => {
      if (isItemInvalid(item)) {
        return 'You do not have permission to run structure prediction on this AA sequence';
      }
      return undefined;
    },
    [isItemInvalid]
  );

  const handleRemoveItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleClearAll = useCallback(() => {
    setItems([]);
  }, []);

  const handleWorklistSelect = async (worklist: {id: string} | null): Promise<void> => {
    if (worklist == null) {
      return;
    }

    try {
      const fullWorklist = await fetchWorklist(worklist.id);
      const allItemIds = fullWorklist.itemIds;
      const existingIds = new Set(items.map((item) => item.id));
      const candidateItems: Array<BulkPredictionItem> = [];
      let skippedDuplicates = 0;
      let skippedIncompatible = 0;

      for (const itemId of allItemIds) {
        const entityType = APIUtil.apiIdToType(itemId);
        if (!isEntityTypeCompatible(entityType, inputTypes)) {
          skippedIncompatible++;
        } else if (existingIds.has(itemId)) {
          skippedDuplicates++;
        } else {
          candidateItems.push({id: itemId, entityType});
        }
      }

      const remainingCapacity = maxBulkPredictionItems - items.length;
      if (candidateItems.length > remainingCapacity) {
        notifications.error(
          `Cannot add worklist — ${candidateItems.length} items would exceed the limit of ${maxBulkPredictionItems}. Remove some items first.`
        );
        return;
      }

      const proteinIds = candidateItems.flatMap((item) =>
        item.entityType === ModelTypeConstants.PROTEIN ? [item.id] : []
      );
      const editableProteinIds = new Set<string>();
      let skippedInvalid = 0;

      if (proteinIds.length > 0) {
        const proteinResult = await doQuerySafe(PROTEIN_STRUCTURE_PREDICTION_PERMISSIONS_QUERY_WRAPPER, {
          variables: {ids: proteinIds},
        });
        proteinResult.proteins.forEach((protein) => {
          if (
            protein.__typename === 'ProteinNode' &&
            protein.permissions.structurePredictionEditable === true
          ) {
            editableProteinIds.add(protein.id);
          }
        });
      }

      const compatible: Array<BulkPredictionItem> = [];
      for (const item of candidateItems) {
        if (item.entityType === ModelTypeConstants.PROTEIN && !editableProteinIds.has(item.id)) {
          skippedInvalid++;
        } else {
          compatible.push(item);
        }
      }

      setItems((prev) => [...prev, ...compatible]);

      if (skippedIncompatible > 0 || skippedInvalid > 0 || skippedDuplicates > 0) {
        const parts: Array<string> = [];
        if (skippedIncompatible > 0) {
          parts.push(
            `${skippedIncompatible} incompatible ${pluralize(skippedIncompatible, 'item', 'items')} skipped`
          );
        }
        if (skippedInvalid > 0) {
          parts.push(`${skippedInvalid} invalid ${pluralize(skippedInvalid, 'item', 'items')} skipped`);
        }
        if (skippedDuplicates > 0) {
          parts.push(
            `${skippedDuplicates} duplicate ${pluralize(skippedDuplicates, 'item', 'items')} skipped`
          );
        }
        notifications.info(`${compatible.length} of ${allItemIds.length} items added (${parts.join(', ')})`);
      }
    } catch (e) {
      notifications.error('Failed to load worklist items.');
      if (!(e instanceof Error)) {
        throw e;
      }
    }
  };

  const validateAndSetRange = (newMin: number | null, newMax: number | null): void => {
    let message: string | null = null;
    if (newMin == null || newMax == null) {
      message = 'Minimum and maximum sequence lengths must be specified.';
    } else if (newMin < 1) {
      message = 'Minimum sequence length must be at least 1.';
    } else if (newMax > MAX_BOLTZGEN_DESIGN_PROTEIN_LENGTH) {
      message = `Maximum sequence length cannot exceed ${MAX_BOLTZGEN_DESIGN_PROTEIN_LENGTH}.`;
    } else if (newMin > newMax) {
      message = 'Minimum sequence length cannot be greater than maximum sequence length.';
    }
    setRangeValidationMessage(message);
    if (newMin != null) {
      setMinLength(newMin);
    }
    if (newMax != null) {
      setMaxLength(newMax);
    }
  };

  const getDisabledReason = (): string | null => {
    if (isSubmitting) {
      return 'Submitting...';
    }
    if (selectedFolderId == null) {
      return 'Please select a folder';
    }
    if (items.length === 0) {
      return 'Please add at least one item';
    }
    if (isBoltzGen && rangeValidationMessage != null) {
      return rangeValidationMessage;
    }
    return null;
  };

  const disabledReason = getDisabledReason();

  const handleSubmit = async (): Promise<void> => {
    if (disabledReason != null || selectedFolderId == null) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setCreditError(null);

    try {
      const useMsa = getUseMSAForSubmission(model, msaEnabled);
      const modelParams = {
        ...(isBoltzGen ? {minLength, maxLength} : {}),
        useMsa,
      };

      await submitBulkPrediction({
        folderId: selectedFolderId,
        model,
        items,
        modelParams,
        source: MODEL_RUN_SOURCE.MODEL_HUB_UI,
        ...(batchName ? {name: batchName} : {}),
      });

      FolderDefaults.set(FolderDefaultsKey.BULK_MODEL_PREDICTION, selectedFolderId);
      notifications.success(`${displayName} run created with ${items.length} predictions.`);
      onRequestSubmit(true);
    } catch (e) {
      const creditsErr = getInsufficientCreditsAgentTypeError(e);
      if (creditsErr) {
        setCreditError(creditsErr);
        return;
      }
      if (!(e instanceof Error)) {
        throw e;
      }
      setError(e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const outputFolderSection = (
    <div className={classes.section}>
      <div className={classes.labelWithTooltip}>
        <Text className={classes.sectionLabel} mb={8}>
          Output Folder{' '}
          <Text component="span" color="red">
            *
          </Text>
        </Text>
        <Tooltip text="The folder determines who can see the prediction batch. However, prediction batches won't appear in folders yet.">
          <span
            tabIndex={0}
            role="button"
            aria-label="More information about Output Folder"
            className={classes.infoIconWrapper}
          >
            <BenchlingIcon icon="info-circle" color={GRAY_SEMIDARK} />
          </span>
        </Tooltip>
      </div>
      <FolderSelector
        value={selectedFolderId}
        onChange={(e: {target: {value: string | null}}) => {
          setSelectedFolderId(e.target.value);
        }}
        allowNone={false}
        enableRedesign={false}
        error=""
        level="append"
        noFolderText="Choose folder"
        readOnly={false}
        requireSubfolderCreatableAuth={false}
        showInventory={false}
        showNotebook={true}
        showRegistries={false}
        unsetOnFetchFail={false}
        checkExistingItemForDataModsMovement={false}
      />
    </div>
  );

  const batchNameSection = (
    <div className={classes.section}>
      <TextInput
        label="Batch Name"
        value={batchName}
        onChange={(e) => {
          setBatchName(e.currentTarget.value);
        }}
        placeholder="Enter a name for this prediction batch"
        maxLength={255}
      />
    </div>
  );

  const modelParametersSection = (
    <div className={classes.section}>
      <Text className={classes.sectionLabel} mb={8}>
        Model Parameters
      </Text>
      {isBoltzGen && (
        <div className={classes.modelParameterGroup}>
          <Text size="sm" color="dimmed" mb={4}>
            Generated Sequence Length Range
          </Text>
          <div className={classes.paramRow}>
            <div className={classes.paramField}>
              <Text size="sm" color="dimmed">
                Minimum
              </Text>
              <NumericInput
                className={classes.numericInput}
                value={minLength}
                required={true}
                onChange={(e: NumericInputChangeEvent) => {
                  validateAndSetRange(e.target.numericValue, maxLength);
                }}
              />
            </div>
            <div className={classes.paramField}>
              <Text size="sm" color="dimmed">
                Maximum
              </Text>
              <NumericInput
                className={classes.numericInput}
                value={maxLength}
                required={true}
                onChange={(e: NumericInputChangeEvent) => {
                  validateAndSetRange(minLength, e.target.numericValue);
                }}
              />
            </div>
          </div>
          {rangeValidationMessage && (
            <div className={classes.validationMessage}>{rangeValidationMessage}</div>
          )}
        </div>
      )}
      <MSAOptions model={model} msaEnabled={msaEnabled} onMSAEnabledChange={setMSAEnabled} />
    </div>
  );

  return (
    <Modal
      title={
        <div className={classes.titleRow}>
          <img src={iconUrl} alt={displayName} className={classes.titleIcon} />
          <span>Configure {displayName} Batch Run</span>
        </div>
      }
      onRequestClose={onRequestClose}
      closeableOnBackgroundClick={!isSubmitting}
      fullWidth={true}
      fullHeight={true}
      bodyPadded={false}
      scrollable={false}
    >
      <div className={classes.outerContainer}>
        <div className={classes.content}>
          <Alert color="blue" mb="md">
            Run a batch of up to {maxBulkPredictionItems} items.
            {modelSupportsMultimers && ' Multimer support for batches coming soon.'}
          </Alert>
          {creditError && (
            <div className={classes.errorBanner}>
              <InlineBanner
                bannerType={InlineBannerType.WARNING}
                text={creditError.title}
                supportingInfo={creditError.detail}
              />
            </div>
          )}
          {error && (
            <div className={classes.errorBanner}>
              <InlineBanner text={error} bannerType={InlineBannerType.ERROR} />
            </div>
          )}

          {/* Form fields in a single row — 2 columns without model params, 3 with */}
          <Group grow={true} align="flex-start" spacing="xl">
            {outputFolderSection}
            {batchNameSection}
            {showModelParameters && modelParametersSection}
          </Group>

          {/* Selected Entities */}
          <SelectedEntitiesArea
            items={items}
            maxBulkPredictionItems={maxBulkPredictionItems}
            onRemoveItem={handleRemoveItem}
            onClearAll={handleClearAll}
          />

          {/* Tabs: Search / Add from worklist */}
          <div className={classes.searchArea}>
            <Tabs
              value={activeTab}
              onTabChange={(value) => {
                if (value != null) {
                  setActiveTab(value);
                }
              }}
              variant="pills"
              classNames={tabClasses}
            >
              <Tabs.List>
                <Tabs.Tab value={TabValue.SEARCH} icon={<SearchTabIcon />}>
                  Search
                </Tabs.Tab>
                <Tabs.Tab value={TabValue.WORKLIST} icon={<WorklistTabIcon />}>
                  Add from worklist
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value={TabValue.SEARCH}>
                <div className={classes.searchViewContainer}>
                  <BioentitySearchView
                    navSection={Section.MODEL_HUB_STRUCTURE_PREDICTION}
                    availableTypes={availableTypes}
                    selectedBioentityIds={selectedBioentityIds}
                    setSelectedBioentityIds={handleBioentitySelectionChange}
                    rowSelectionMode={RowSelectionMode.MULTIPLE}
                    limitSelectedEntitiesParams={
                      isAtCapacity
                        ? {maxNumEntitiesToAdd: maxBulkPredictionItems, selectedEntityName: 'entities'}
                        : undefined
                    }
                    isItemInvalid={isItemInvalid}
                    getInvalidItemTooltipText={getInvalidItemTooltipText}
                    showInvalid={true}
                  />
                </div>
              </Tabs.Panel>

              <Tabs.Panel value={TabValue.WORKLIST}>
                <div className={classes.worklistContent}>
                  <div className={classes.worklistSearchContainer}>
                    <WorklistSearchInput
                      autoFocus={false}
                      disabled={isAtCapacity}
                      onChange={handleWorklistSelect}
                      worklistTypes={[WorklistType.BIOENTITY]}
                    />
                  </div>
                </div>
              </Tabs.Panel>
            </Tabs>
          </div>
        </div>

        {/* Footer */}
        <div className={classes.footer}>
          <Button
            buttonStyle={ButtonStyle.TEXT}
            size={ButtonSize.MEDIUM}
            text="Cancel"
            onClick={onRequestClose}
          />
          <Tooltip text={disabledReason} isDisabled={disabledReason == null}>
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.MEDIUM}
              text={
                isSubmitting
                  ? 'Submitting...'
                  : items.length === 1
                    ? 'Run 1 prediction'
                    : `Run ${items.length} predictions`
              }
              disabled={disabledReason != null}
              onClick={handleSubmit}
            />
          </Tooltip>
        </div>
      </div>
    </Modal>
  );
}

function SearchTabIcon(): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

function WorklistTabIcon(): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

const useStyles = createStyles((theme) => ({
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  titleIcon: {
    width: 24,
    height: 24,
    objectFit: 'contain' as const,
  },
  section: {
    marginBottom: theme.spacing.md,
  },
  sectionLabel: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
  },
  labelWithTooltip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  infoIconWrapper: {
    cursor: 'help',
    display: 'inline-flex',
    marginBottom: theme.spacing.xs,
  },
  paramRow: {
    display: 'flex',
    gap: theme.spacing.md,
  },
  paramField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  modelParameterGroup: {
    marginBottom: theme.spacing.sm,
  },
  numericInput: {
    width: 100,
  },
  validationMessage: {
    color: RED,
    fontSize: theme.fontSizes.sm,
    marginTop: 4,
  },
  searchArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  searchViewContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    marginLeft: `calc(-1 * ${theme.spacing.xl})`,
    marginRight: `calc(-1 * ${theme.spacing.xl})`,
  },
  worklistContent: {
    padding: `${theme.spacing.md} 0`,
  },
  worklistSearchContainer: {
    maxWidth: 350,
  },
  errorBanner: {
    marginBottom: theme.spacing.md,
  },
  footer: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

const useTabStyles = createStyles((theme) => ({
  root: {display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden'},
  tabsList: {gap: 0, borderBottom: 'none', marginBottom: theme.spacing.md},
  tab: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 500,
    padding: `${theme.spacing.sm} ${theme.spacing.xl}`,
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: 0,
    color: theme.colors.gray[7],
    '&:first-of-type': {
      borderTopLeftRadius: theme.radius.lg,
      borderBottomLeftRadius: theme.radius.lg,
    },
    '&:last-of-type': {
      borderTopRightRadius: theme.radius.lg,
      borderBottomRightRadius: theme.radius.lg,
      borderLeft: 'none',
    },
    '&[data-active]': {
      backgroundColor: theme.colors.blue[0],
      color: theme.colors.blue[7],
      fontWeight: 600,
      borderColor: theme.colors.blue[4],
    },
    '&:not([data-active])': {
      backgroundColor: theme.white,
    },
  },
  tabIcon: {
    marginRight: theme.spacing.sm,
  },
  panel: {flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden'},
}));
